/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// mantine
import {
	Modal,
	Button,
	Group,
	Stack,
	ModalProps,
} from '@mantine/core';
import { useForm } from '@mantine/form';

// local deps
import { Select, TextInputV2 } from '@components/atoms';

// styles
import classes from './ModalSendCertificate.module.css';

// lodash
import concat from 'lodash/concat';
import { REGEX_EMAIL } from '@config/constants/regex';
import { useTranslation } from 'react-i18next';
import isNil from 'lodash/isNil';
import { TSelectOption } from '@global-types/index';
import { useEffect } from 'react';

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */
const LANGUAGES_OPTIONS = [
	{ label: 'French', value: 'fr' },
	{ label: 'English', value: 'en' },
];

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */
type TModalSendCertificateProps = Partial<ModalProps> & {
	recepientOptions: TSelectOption[];
	handlesubmit: (values: {
		other?: string;
		recepient: string | null;
		language: string;
	}) => void;
	handleclose: () => void;
	loading?: boolean;
	opened: boolean;
};
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ModalSendCertificate = (
	props: TModalSendCertificateProps
) => {
	/* ******************************** Hooks ******************************* */
	const { t } = useTranslation();

	const form = useForm({
		initialValues: {
			recepient: null,
			other: '',
			language: 'fr',
		},

		validate: {
			recepient: (value) =>
				isNil(value)
					? t('global.recepientRequired')
					: null,
			other: (value, values) =>
				!REGEX_EMAIL.test(value) &&
				values.recepient == 'other'
					? t('form.error.invalidEmail')
					: null,
			language: (value) =>
				isNil(value)
					? t('global.languageRequired')
					: null,
		},
	});

	const recipientOptions = concat(
		[],
		props.recepientOptions,
		{
			label: t('global.other'),
			value: 'other',
		}
	);
	const handleClose = () => {
		props.handleclose();
		form.reset();
	};

	useEffect(() => {
		if (!props.opened) {
			handleClose();
		}
	}, [props.opened]);

	return (
		<Modal
			title={t('global.sendMailForm')}
			classNames={{ title: classes.title }}
			opened={props.opened}
			onClose={handleClose}
		>
			<form onSubmit={form.onSubmit(props.handlesubmit)}>
				<Stack gap={'xs'}>
					<Select
						notruncate
						withAsterisk
						data={recipientOptions}
						label={t('global.recipientOptions')}
						placeholder={t('global.recipientOptions')}
						{...form.getInputProps('recepient')}
					/>
					{form.getValues().recepient === 'other' && (
						<TextInputV2
							withAsterisk
							label={t('global.otherMail')}
							{...form.getInputProps('other')}
							placeholder={t(
								'placeholders.enterRecipientEmail'
							)}
						/>
					)}
					<Select
						data={LANGUAGES_OPTIONS}
						label={t('global.language')}
						placeholder={t('global.language')}
						{...form.getInputProps('language')}
					/>
				</Stack>
				<Group mt="lg" justify="flex-end">
					<Button onClick={handleClose} variant="default">
						{t('global.cancel')}
					</Button>
					<Button
						disabled={props.loading}
						loading={props.loading}
						type="submit"
					>
						{t('global.send')}
					</Button>
				</Group>
			</form>
		</Modal>
	);
};
